import React, { FC } from "react";
import { GeometryType } from "@evergis/api";
import { Description, IconButton } from "@evergis/ui";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { EditFeatureCoordinatesButton } from "./components/EditFeatureCoordinatesButton";
import { PolylineControls } from "./PolylineControls";
import { PolygonControls } from "./PolygonControls";
import { MultiPointControls } from "./MultiPointControls";
import { EditText, UndosContainer } from "../../styled";

import { getGeometryEditorMode } from "../../../../ducks/geometryEditor/selectors";
import { useFeatureEditHistory } from "../../hooks/useFeatureEditHistory";

import { ControlsProps, GeometryEditAllowType } from "./types";
import { FeatureDcExtended } from "../../../../ducks/selectFeatures/types";

interface EditControlsProps {
  geometryType: GeometryType;
  toggleEditMode: VoidFunction;
  saveChanges: VoidFunction;
  isRaster?: boolean;
  feature: FeatureDcExtended;
}

const CONTROLS_BY_TYPE: Record<GeometryEditAllowType, FC<ControlsProps>> = {
  [GeometryType.Polyline]: PolylineControls,
  [GeometryType.Polygon]: PolygonControls,
  [GeometryType.Multipoint]: MultiPointControls,
};

export const EditControls: FC<EditControlsProps> = ({
  geometryType,
  isRaster,
  toggleEditMode,
  saveChanges,
  feature,
}) => {
  const { t } = useTranslation("common");
  const mode = useSelector(getGeometryEditorMode);
  const [, { undo, redo, canUndo, canRedo }] = useFeatureEditHistory();
  const GeometryControls = CONTROLS_BY_TYPE[geometryType as GeometryEditAllowType];

  return (
    <>
      {!isRaster && GeometryControls ? (
        <GeometryControls mode={mode} feature={feature} />
      ) : (
        <EditText>
          {geometryType === GeometryType.Unknown ? (
            <Description>{t("withoutGeometry")}</Description>
          ) : (
            <EditFeatureCoordinatesButton />
          )}
        </EditText>
      )}
      {!isRaster && (
        <UndosContainer>
          <IconButton kind={"step_back"} onClick={undo} disabled={!canUndo} />
          <IconButton kind={"step_forward"} onClick={redo} disabled={!canRedo} />
        </UndosContainer>
      )}
      <IconButton error kind={"close"} onClick={toggleEditMode} />
      <IconButton primary kind={"success"} onClick={saveChanges} />
    </>
  );
};
